import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Download, ArrowRight, Chrome, Eye, RefreshCw, Settings, MessageCircle, Zap, Type, Target, Sparkles, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const manualInstallSteps = [
  {
    num: 1,
    title: 'Download the extension',
    desc: 'Click the button below to download the SendPI Chrome Extension zip file.',
    isDownload: true,
  },
  {
    num: 2,
    title: 'Unzip the file',
    desc: 'Find the downloaded sendpi-extension.zip file and unzip/extract it to a folder on your computer.',
    tip: 'On Mac: double-click the zip. On Windows: right-click → Extract All.',
  },
  {
    num: 3,
    title: 'Open Chrome Extensions',
    desc: 'Open Chrome and go to chrome://extensions in the address bar. Or click the three dots menu → Extensions → Manage Extensions.',
    tip: 'You can also type chrome://extensions directly in your address bar.',
  },
  {
    num: 4,
    title: 'Enable Developer Mode',
    desc: 'Toggle the "Developer mode" switch in the top-right corner of the extensions page. This is required to load the extension manually.',
  },
  {
    num: 5,
    title: 'Load the extension',
    desc: 'Click "Load unpacked" button that appears in the top-left. Select the folder where you unzipped the extension files.',
    tip: 'Select the folder that contains manifest.json — not the zip file itself.',
  },
  {
    num: 6,
    title: 'Pin SendPI to your toolbar',
    desc: 'Click the puzzle icon in Chrome\'s toolbar, find SendPI, and click the pin icon so it\'s always visible. You\'re ready to go!',
  },
];

const extensionFeatures = [
  {
    icon: Eye,
    title: 'Real-time PI Score',
    desc: 'See your 0-100 score update as you type. Know exactly how your message will land before hitting send.',
  },
  {
    icon: Zap,
    title: '15+ Emotional Signals',
    desc: 'Detects aggressive, passive-aggressive, guilt-tripping, over-apologizing, sarcasm, dismissive, and more.',
  },
  {
    icon: RefreshCw,
    title: 'One-click Rewrite',
    desc: '7 tones, 12 intents — choose how you want to come across and SendPI rewrites it for you.',
  },
  {
    icon: Target,
    title: 'Context Modes',
    desc: 'Set your profession and relationship (boss, client, team) for context-aware rewrites.',
  },
  {
    icon: Sparkles,
    title: 'PI Craft',
    desc: 'Turn rough AI instructions into precision casts for ChatGPT, Claude, Gemini, and more.',
  },
  {
    icon: MessageCircle,
    title: 'Works Everywhere',
    desc: 'Gmail, Slack, LinkedIn, WhatsApp Web, Twitter/X, Reddit — any text field on any website.',
  },
];

const platformGuides = [
  {
    name: 'Gmail',
    steps: ['Open Gmail and compose a new email', 'Start typing your message', 'SendPI panel appears at the bottom-right of the compose window', 'Check your PI Score and rewrite if needed before clicking Send'],
  },
  {
    name: 'Slack',
    steps: ['Open any Slack channel or DM', 'Click the message input and start typing', 'SendPI floating panel appears near the input field', 'Great for preventing Slack misunderstandings in async communication'],
  },
  {
    name: 'LinkedIn',
    steps: ['Open LinkedIn messaging or post composer', 'Type your message or post', 'SendPI analyzes tone — especially useful for cold outreach and connection requests', 'Rewrite to sound professional and get more replies'],
  },
  {
    name: 'WhatsApp Web',
    steps: ['Open web.whatsapp.com in Chrome', 'Select a conversation and start typing', 'SendPI appears to analyze your message tone', 'Perfect for sensitive personal or professional conversations'],
  },
];

const troubleshooting = [
  { q: 'Extension not appearing on a page?', a: 'Refresh the page after installing. Some pages loaded before the extension was installed won\'t detect it until refreshed. Also check chrome://extensions to make sure SendPI is enabled.' },
  { q: 'The floating panel is blocking my text?', a: 'You can drag the panel to any position on the screen. Click and hold the top bar to move it. You can also minimize it by clicking the collapse arrow.' },
  { q: '"Load unpacked" button not visible?', a: 'Make sure "Developer mode" is toggled ON in the top-right corner of chrome://extensions page.' },
  { q: 'Extension not working on a specific site?', a: 'Some sites (like Google Docs) use custom editors that don\'t expose standard text fields. We\'re actively expanding compatibility. Gmail, Slack, LinkedIn, and WhatsApp Web are fully supported.' },
  { q: 'How do I update the extension?', a: 'Download the latest zip from this page, unzip it to the same folder (overwrite), then go to chrome://extensions and click the refresh icon on the SendPI card.' },
];

export default function InstallPage() {
  const [downloaded, setDownloaded] = useState(false);

  const handleDownload = () => {
    setDownloaded(true);
    window.open(`${API_URL}/sendpi-extension.zip`, '_blank');
  };

  const scrollToSteps = () => {
    document.getElementById('install-steps')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-testid="install-page">
      <SEO title="Install Chrome Extension" description="Install the SendPI Chrome Extension. Download the zip, load it into Chrome, and start predicting how your messages land. Free, takes 60 seconds." path="/install" />
      <Navbar />

      <main className="pt-24 pb-16">
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-white/50 text-xs font-space mb-6">
            <Chrome className="w-4 h-4 text-[var(--pi-cyan)]" /> Chrome Extension
          </div>
          <h1 className="font-space text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mb-4">
            Set up SendPI in <span className="pi-gradient-text">60 seconds</span>
          </h1>
          <p className="text-white/45 text-base max-w-2xl mx-auto mb-8">
            Download and install the Chrome extension manually. Start predicting how your messages will land — on Gmail, Slack, LinkedIn, WhatsApp, and any website.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={handleDownload}
              className="shimmer-btn text-white rounded-full px-10 py-6 text-lg font-space font-bold border-0"
              data-testid="hero-install-btn"
            >
              <Download className="w-5 h-5 mr-2" /> Download Extension
            </Button>
            <Button
              onClick={scrollToSteps}
              variant="outline"
              className="rounded-full px-8 py-6 border-white/10 text-white/60 hover:bg-white/5 font-space"
              data-testid="hero-steps-btn"
            >
              See install steps <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>

        {/* Manual Install Steps */}
        <section id="install-steps" className="max-w-4xl mx-auto px-6 md:px-12 mb-24" data-testid="install-steps">
          <h2 className="font-space text-2xl sm:text-3xl text-white text-center mb-4 font-bold">Installation Steps</h2>
          <p className="text-white/40 text-sm text-center mb-12 max-w-lg mx-auto">Follow these steps to manually install the SendPI Chrome Extension. It takes about 60 seconds.</p>

          <div className="space-y-4">
            {manualInstallSteps.map((step) => (
              <div key={step.num} className="glass-card rounded-2xl p-6 md:p-8" data-testid={`install-step-${step.num}`}>
                <div className="flex items-start gap-5">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-space font-bold text-lg ${
                    downloaded && step.num === 1 ? 'bg-emerald-500/20 border-2 border-emerald-500/40' : 'pi-gradient'
                  }`}>
                    {downloaded && step.num === 1 ? <Check className="w-6 h-6 text-emerald-400" /> : step.num}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-space font-bold text-lg mb-2">{step.title}</h3>
                    <p className="text-white/45 text-sm mb-4">{step.desc}</p>
                    {step.tip && (
                      <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--pi-violet)]/[0.06] border border-[var(--pi-violet)]/15 text-[var(--pi-violet)] text-xs">
                        <Zap className="w-3.5 h-3.5" /> {step.tip}
                      </div>
                    )}
                    {step.isDownload && (
                      <Button
                        onClick={handleDownload}
                        className={`rounded-full px-6 text-sm font-space font-bold border-0 ${
                          downloaded ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'shimmer-btn text-white'
                        }`}
                        data-testid="step-1-download-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {downloaded ? 'Downloaded! Now unzip it' : 'Download sendpi-extension.zip'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick visual summary */}
          <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-[#8B5CF6]/5 to-[#06B6D4]/5 border border-white/[0.06]">
            <div className="flex items-center gap-3 mb-3">
              <Chrome className="w-5 h-5 text-[var(--pi-cyan)]" />
              <span className="text-white font-space font-bold text-sm">Quick Summary</span>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              {['Download zip', 'Unzip', 'chrome://extensions', 'Developer Mode ON', 'Load unpacked', 'Pin to toolbar'].map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                  <span className="text-xs text-white/50 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06]">{s}</span>
                  {i < 5 && <ArrowRight className="w-3 h-3 text-white/15" />}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What the extension does */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 mb-24" data-testid="extension-features">
          <h2 className="font-space text-2xl sm:text-3xl text-white text-center mb-12 font-bold">What the extension does</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {extensionFeatures.map((f) => (
              <div key={f.title} className="glass-card rounded-2xl p-6 group">
                <div className="w-10 h-10 rounded-lg pi-gradient flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <f.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-space text-white font-bold text-sm mb-2">{f.title}</h3>
                <p className="text-white/40 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Platform-specific guides */}
        <section className="max-w-4xl mx-auto px-6 md:px-12 mb-24" data-testid="platform-guides">
          <h2 className="font-space text-2xl sm:text-3xl text-white text-center mb-12 font-bold">
            Using SendPI on <span className="pi-gradient-text">your favorite platforms</span>
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {platformGuides.map((platform) => (
              <div key={platform.name} className="glass-card rounded-2xl p-6" data-testid={`guide-${platform.name.toLowerCase()}`}>
                <h3 className="font-space text-white font-bold text-base mb-4">{platform.name}</h3>
                <ol className="space-y-3">
                  {platform.steps.map((step, i) => (
                    <li key={`${platform.name}-step-${i}`} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white/[0.06] flex items-center justify-center text-white/50 text-xs font-bold">{i + 1}</span>
                      <span className="text-white/50 text-sm leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </section>

        {/* How the extension connects */}
        <section className="max-w-4xl mx-auto px-6 md:px-12 mb-24">
          <div className="bg-[var(--bg-elevated)] border border-white/[0.06] rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <Settings className="w-5 h-5 text-[var(--pi-cyan)]" />
              <h3 className="text-white font-space font-bold">How it connects to your account</h3>
            </div>
            <div className="space-y-4 text-white/45 text-sm leading-relaxed">
              <p>Open the extension popup, enter your email, and verify with a 6-digit code. Your account syncs instantly.</p>
              <div className="grid sm:grid-cols-3 gap-4 pt-2">
                <div className="bg-white/[0.03] rounded-xl p-4 border border-white/[0.06]">
                  <p className="text-emerald-400 text-xs font-bold mb-1">Privacy first</p>
                  <p className="text-white/40 text-xs">Your message content never leaves your device. Zero storage.</p>
                </div>
                <div className="bg-white/[0.03] rounded-xl p-4 border border-white/[0.06]">
                  <p className="text-[var(--pi-cyan)] text-xs font-bold mb-1">Email login</p>
                  <p className="text-white/40 text-xs">No password needed. Enter email, get code, connect instantly.</p>
                </div>
                <div className="bg-white/[0.03] rounded-xl p-4 border border-white/[0.06]">
                  <p className="text-[var(--pi-violet)] text-xs font-bold mb-1">Works offline</p>
                  <p className="text-white/40 text-xs">Basic emotion detection runs locally even without internet.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Troubleshooting */}
        <section className="max-w-3xl mx-auto px-6 md:px-12" data-testid="troubleshooting-section">
          <h2 className="font-space text-2xl sm:text-3xl text-white text-center mb-8 font-bold">Troubleshooting</h2>
          <Accordion type="single" collapsible className="space-y-4">
            {troubleshooting.map((item) => (
              <AccordionItem key={item.q} value={`trouble-${item.q.slice(0, 20)}`} className="border-white/[0.06] bg-[var(--bg-elevated)] rounded-xl px-6">
                <AccordionTrigger className="text-white hover:text-white/80 text-left font-space">{item.q}</AccordionTrigger>
                <AccordionContent className="text-white/45">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 text-center">
            <p className="text-white/30 text-sm mb-4">Still having issues?</p>
            <Link to="/dashboard/support">
              <Button variant="outline" className="border-white/10 text-white/60 hover:bg-white/5 rounded-full px-6">Contact support</Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
